import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import SearchableDropdown from '../../../../../components/SearchableDropdown';

const ChargeHeadModal = ({
    show,
    handleClose,
    handleSubmit,
    formData,
    setFormData,
    editId,
    headTypes,
    calculationTypes,
    unitTypes,
    taxRulesData,
    coaDropdown,
    coaDropdownLoading,
    loading
}) => {
    const [glSearch, setGlSearch] = useState('');
    const [glDropdownOpen, setGlDropdownOpen] = useState(false);
    if (!show) return null;

    const isSystemGenerated = ["PRINCIPAL_ARREAR", "DEBIT_NOTE_ARREAR", "ADJUSTMENT", "INTEREST", "CGST", "SGST", "IGST", "ROUND_OFF"].includes(formData?.head_type_name);

    return (
        <div className="ch-modal-backdrop" onClick={handleClose}>
            <div className="ch-modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="ch-modal-header">
                    <span className="ch-modal-title">{editId ? 'Edit' : 'Add'} Charge Head</span>
                    <button onClick={handleClose} className="ch-modal-close">
                        <Icon icon="mdi:close" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="ch-modal-body">
                        {/* Head Name */}
                        <div className="ch-row">
                            <div className="ch-field ch-full">
                                <label className="ch-label">Head Name <span className="ch-req">*</span></label>
                                <input
                                    className="ch-input"
                                    value={formData.head_name}
                                    onChange={(e) => setFormData({ ...formData, head_name: e.target.value })}
                                    placeholder="e.g., Maintenance Charge"
                                    required
                                />
                            </div>
                        </div>

                        {/* Head Code & GL Code */}
                        <div className="ch-row">
                            <div className="ch-field ch-half">
                                <label className="ch-label">Head Code <span className="ch-req">*</span></label>
                                <input
                                    className="ch-input"
                                    value={formData.head_code}
                                    onChange={(e) => setFormData({ ...formData, head_code: e.target.value.toUpperCase() })}
                                    placeholder="e.g., MAINT"
                                    required
                                />
                            </div>
                            <div className="col-6">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    GL Code <span className="text-danger">*</span>
                                </label>
                                <div className="position-relative">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={coaDropdownLoading ? "Loading accounts..." : "Search GL account..."}
                                        value={glSearch || (formData.gl_code ? (coaDropdown?.find(a => a.ac_code === formData.gl_code)?.label || formData.gl_code) : '')}
                                        onChange={(e) => {
                                            setGlSearch(e.target.value);
                                            setGlDropdownOpen(true);
                                            if (!e.target.value) setFormData({ ...formData, gl_code: '' });
                                        }}
                                        onFocus={() => { setGlDropdownOpen(true); setGlSearch(''); }}
                                        onBlur={() => setTimeout(() => setGlDropdownOpen(false), 200)}
                                        disabled={coaDropdownLoading}
                                        required
                                    />
                                    {coaDropdownLoading && (
                                        <span className="position-absolute end-0 top-50 translate-middle-y me-10">
                                            <span className="spinner-border spinner-border-sm text-primary" />
                                        </span>
                                    )}
                                    {glDropdownOpen && (coaDropdown?.length > 0) && (
                                        <div className="position-absolute w-100 bg-white border radius-8 shadow-sm" style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto', top: '100%' }}>
                                            {coaDropdown
                                                .filter(a => !glSearch || a.label.toLowerCase().includes(glSearch.toLowerCase()))
                                                .map(account => (
                                                    <div
                                                        key={account.id}
                                                        className={`px-12 py-8 cursor-pointer hover-bg-primary-50 ${formData.gl_code === account.ac_code ? 'bg-primary-50 text-primary-600 fw-semibold' : ''}`}
                                                        style={{ cursor: 'pointer', fontSize: '13px' }}
                                                        onMouseDown={() => {
                                                            setFormData({ ...formData, gl_code: account.ac_code });
                                                            setGlSearch('');
                                                            setGlDropdownOpen(false);
                                                        }}
                                                    >
                                                        <div className="fw-medium">{account.label}</div>
                                                        {account.tag && <div className="text-xs text-secondary-light">{account.tag}</div>}
                                                    </div>
                                                ))
                                            }
                                            {coaDropdown.filter(a => !glSearch || a.label.toLowerCase().includes(glSearch.toLowerCase())).length === 0 && (
                                                <div className="px-12 py-8 text-secondary-light text-sm">No accounts found</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {!formData.gl_code && <div className="text-danger text-xs mt-1">GL Code is required</div>}
                            </div>
                        </div>

                        {/* Head Type & Calculation Type */}
                        {!isSystemGenerated && (
                            <div className="ch-row">
                                <div className="ch-field ch-half">
                                    <label className="ch-label">Head Type <span className="ch-req">*</span></label>
                                    <SearchableDropdown
                                        value={formData.head_type_code}
                                        onChange={(val) => setFormData({ ...formData, head_type_code: val })}
                                        options={headTypes}
                                        valueKey="code"
                                        labelKey="name"
                                        placeholder="Select Head Type"
                                        clearable={false}
                                        small
                                    />
                                </div>
                                <div className="ch-field ch-half">
                                    <label className="ch-label">Calculation Type <span className="ch-req">*</span></label>
                                    <SearchableDropdown
                                        value={formData.calculation_type}
                                        onChange={(val) => setFormData({ ...formData, calculation_type: val })}
                                        options={calculationTypes}
                                        valueKey="value"
                                        labelKey="label"
                                        placeholder="Select Calc Type"
                                        clearable={false}
                                        small
                                    />
                                </div>
                            </div>
                        )}

                        {/* Applies To (property type) */}
                        {!isSystemGenerated && (
                            <div className="ch-row">
                                <div className="ch-field ch-full">
                                    <label className="ch-label">Applies To</label>
                                    <SearchableDropdown
                                        value={formData.unit_type || 'ALL'}
                                        onChange={(val) => setFormData({ ...formData, unit_type: val })}
                                        options={unitTypes || []}
                                        valueKey="value"
                                        labelKey="label"
                                        placeholder="Select Property Type"
                                        clearable={false}
                                        small
                                    />
                                    <div className="ch-hint">
                                        {formData.unit_type === 'RESIDENTIAL' && 'Charged only on residential flats.'}
                                        {formData.unit_type === 'COMMERCIAL' && 'Charged only on commercial flats.'}
                                        {(!formData.unit_type || formData.unit_type === 'ALL') && 'Charged on every flat.'}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Display Order & Pure Agent */}
                        <div className="ch-row">
                            <div className="ch-field ch-half">
                                <label className="ch-label">Display Order</label>
                                <input
                                    className="ch-input"
                                    type="number"
                                    value={formData.display_order}
                                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })}
                                    min="1"
                                />
                            </div>
                            {!isSystemGenerated && (
                                <div className="ch-field ch-half">
                                    <label className="ch-label">Pure Agent</label>
                                    <div className="ch-toggle-wrap">
                                        <label className="ch-toggle">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_pure_agent === 1}
                                                onChange={(e) => setFormData({ ...formData, is_pure_agent: e.target.checked ? 1 : 0 })}
                                            />
                                            <span className="ch-toggle-slider"></span>
                                        </label>
                                        <span className="ch-toggle-label">{formData.is_pure_agent === 1 ? 'Yes' : 'No'}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Tax Rule */}
                        <div className="ch-row">
                            <div className="ch-field ch-full">
                                <label className="ch-label">Tax Rule</label>
                                <SearchableDropdown
                                    value={formData.tax_rule_id ? String(formData.tax_rule_id) : 'all'}
                                    onChange={(val) => setFormData({ ...formData, tax_rule_id: val === 'all' ? '' : Number(val) })}
                                    options={taxRulesData || []}
                                    valueKey="tax_rule_id"
                                    labelKey="rule_name"
                                    placeholder="Select Tax Rule"
                                    small
                                />
                                {formData.tax_rule_id && (() => {
                                    const ruleId = Number(formData.tax_rule_id);
                                    const selectedRule = !isNaN(ruleId) ? taxRulesData?.find(r => r.tax_rule_id === ruleId) : null;
                                    if (!selectedRule) return null;
                                    return (
                                        <div className="ch-tax-info">
                                            <div className="ch-tax-info-header">
                                                <span>{selectedRule.rule_name}</span>
                                                <span className="ch-tax-code">{selectedRule.code}</span>
                                            </div>
                                            <div className="ch-tax-rates">
                                                <span>CGST: <strong>{selectedRule.rate_cgst}%</strong></span>
                                                <span>SGST: <strong>{selectedRule.rate_sgst}%</strong></span>
                                                <span>IGST: <strong>{selectedRule.rate_igst}%</strong></span>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Status */}
                        {!isSystemGenerated && (
                            <div className="ch-row">
                                <div className="ch-field ch-full">
                                    <label className="ch-label">Status</label>
                                    <div className="ch-toggle-wrap">
                                        <label className="ch-toggle">
                                            <input
                                                type="checkbox"
                                                checked={formData.status === 1}
                                                onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 1 : 2 })}
                                            />
                                            <span className="ch-toggle-slider"></span>
                                        </label>
                                        <span className="ch-toggle-label">{formData.status === 1 ? 'Active' : 'Inactive'}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="ch-modal-footer">
                        <button type="button" onClick={handleClose} className="ch-btn ch-btn-cancel">Cancel</button>
                        <button type="submit" className="ch-btn ch-btn-submit" disabled={loading}>
                            {loading && <span className="spinner-border spinner-border-sm me-1" role="status" />}
                            <Icon icon={editId ? "mdi:check" : "mdi:plus"} style={{ fontSize: '14px' }} />
                            {editId ? 'Update' : 'Add'}
                        </button>
                    </div>
                </form>
            </div>

            <style>{`
                .ch-modal-backdrop {
                    position: fixed; inset: 0;
                    background: rgba(0,0,0,0.35);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1050;
                }
                .ch-modal-content {
                    background: #fff; border-radius: 10px;
                    width: 100%; max-width: 480px; margin: 16px;
                    max-height: 90vh; overflow-y: auto;
                    box-shadow: 0 8px 30px rgba(0,0,0,0.12);
                }
                .ch-modal-header {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 8px 14px; border-bottom: 1px solid #f0f0f0;
                }
                .ch-modal-title { font-weight: 600; font-size: 12px; color: #1f2937; }
                .ch-modal-close {
                    background: none; border: none; cursor: pointer;
                    color: #9ca3af; font-size: 14px; padding: 2px;
                    display: flex; align-items: center;
                }
                .ch-modal-close:hover { color: #374151; }
                .ch-modal-body { padding: 10px 14px; }
                .ch-row { display: flex; gap: 8px; margin-bottom: 8px; }
                .ch-row:last-child { margin-bottom: 0; }
                .ch-field { display: flex; flex-direction: column; }
                .ch-full { flex: 1; }
                .ch-half { flex: 1; min-width: 0; }
                .ch-label {
                    font-size: 10px; font-weight: 500; color: #6b7280;
                    margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.3px;
                }
                .ch-req { color: #ef4444; }
                .ch-hint { font-size: 11px; color: #9ca3af; margin-top: 4px; }
                .ch-input {
                    height: 30px; padding: 0 8px;
                    border: 1px solid #d1d5db; border-radius: 5px;
                    font-size: 12px; color: #1f2937;
                    outline: none; width: 100%;
                }
                .ch-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59,130,246,0.1); }
                .ch-input::placeholder { color: #9ca3af; }

                /* SearchableDropdown height override */
                .ch-modal-content .tw-relative > button {
                    height: 30px !important; font-size: 12px;
                }

                /* Toggle */
                .ch-toggle-wrap { display: flex; align-items: center; gap: 6px; height: 30px; }
                .ch-toggle { position: relative; width: 32px; height: 18px; cursor: pointer; margin: 0; }
                .ch-toggle input { opacity: 0; width: 0; height: 0; }
                .ch-toggle-slider {
                    position: absolute; inset: 0;
                    background: #d1d5db; border-radius: 9px;
                    transition: 0.2s;
                }
                .ch-toggle-slider::before {
                    content: ''; position: absolute;
                    width: 14px; height: 14px; left: 2px; bottom: 2px;
                    background: #fff; border-radius: 50%;
                    transition: 0.2s;
                }
                .ch-toggle input:checked + .ch-toggle-slider { background: #16a34a; }
                .ch-toggle input:checked + .ch-toggle-slider::before { transform: translateX(14px); }
                .ch-toggle-label { font-size: 12px; color: #6b7280; }

                /* Tax info */
                .ch-tax-info {
                    margin-top: 6px; padding: 8px 10px;
                    background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px;
                }
                .ch-tax-info-header {
                    display: flex; justify-content: space-between; align-items: center;
                    margin-bottom: 4px;
                }
                .ch-tax-info-header span:first-child { font-size: 11px; font-weight: 600; color: #4f46e5; }
                .ch-tax-code {
                    font-size: 10px; background: #eef2ff; color: #4f46e5;
                    padding: 1px 6px; border-radius: 3px;
                }
                .ch-tax-rates { display: flex; gap: 12px; font-size: 11px; color: #6b7280; }

                /* Footer */
                .ch-modal-footer {
                    display: flex; justify-content: flex-end; gap: 6px;
                    padding: 8px 14px; border-top: 1px solid #f0f0f0;
                }
                .ch-btn {
                    display: flex; align-items: center; gap: 4px;
                    padding: 5px 12px; border-radius: 5px;
                    font-size: 11px; font-weight: 500; cursor: pointer; border: none;
                }
                .ch-btn-cancel {
                    background: #fff; border: 1px solid #d1d5db; color: #374151;
                }
                .ch-btn-cancel:hover { background: #f9fafb; }
                .ch-btn-submit {
                    background: #f59e0b; color: #fff;
                }
                .ch-btn-submit:hover { background: #d97706; }
                .ch-btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }
            `}</style>
        </div>
    );
};

export default ChargeHeadModal;
